import EventIcon from '@mui/icons-material/Event';
import { Box, TextField, useTheme } from '@mui/material';
import { createFieldStyles, createLabelProps, FONT_SIZE } from './filter-date-styles';
import { DATE_SEPARATOR, validateDateRange } from './filter-date-utils';
import { ToggleButton } from './toggle-button';

interface DateRangeFieldProps {
  label: string;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  hasValue: boolean;
  allowToggle: boolean;
  onToggle: () => void;
}

export const DateRangeField = ({
  label,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  hasValue,
  allowToggle,
  onToggle,
}: DateRangeFieldProps) => {
  const theme = useTheme();

  const isToDateInvalid = validateDateRange(fromDate, toDate);

  const normalFieldStyle = createFieldStyles(theme, { hasValue });
  const errorFieldStyle = createFieldStyles(theme, { hasValue, hasError: true });
  const normalLabelProps = createLabelProps(theme, { hasValue });
  const errorLabelProps = createLabelProps(theme, { hasValue, hasError: true });

  const toFieldLabelProps = isToDateInvalid ? errorLabelProps : normalLabelProps;
  const toFieldStyle = isToDateInvalid ? errorFieldStyle : normalFieldStyle;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <TextField
        label={`${label} From`}
        type="date"
        value={fromDate || ''}
        onChange={e => onFromDateChange(e.target.value)}
        size="small"
        InputLabelProps={normalLabelProps}
        inputProps={{
          sx: { fontSize: FONT_SIZE },
          ...(toDate ? { max: toDate } : {}),
        }}
        sx={{
          ...normalFieldStyle,
          flex: 1,
          minWidth: 0,
        }}
      />
      <Box
        sx={{
          fontSize: FONT_SIZE,
          color: theme.palette.text.secondary,
          px: 0.5,
          flexShrink: 0,
        }}
      >
        {DATE_SEPARATOR}
      </Box>
      <TextField
        label="To"
        type="date"
        value={toDate || ''}
        onChange={e => onToDateChange(e.target.value)}
        size="small"
        InputLabelProps={toFieldLabelProps}
        inputProps={{
          sx: { fontSize: FONT_SIZE },
          ...(fromDate ? { min: fromDate } : {}),
        }}
        sx={{
          ...toFieldStyle,
          flex: 1,
          minWidth: 0,
        }}
        error={isToDateInvalid}
        helperText={isToDateInvalid ? 'End date must be >= start date' : undefined}
      />
      <ToggleButton
        tooltipTitle="Switch to single date"
        IconComponent={EventIcon}
        onClick={onToggle}
        show={allowToggle}
      />
    </Box>
  );
};
